import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../data/mock';
import { Menu, X, Phone, Globe, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === '/en';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/kurse', label: t('nav.courses') },
    { path: '/ueber-uns', label: t('nav.about') },
    { path: '/bg-kurse', label: t('nav.bgCourses') },
    { path: '/kontakt', label: t('nav.contact') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Header style based on scroll and page
  const getHeaderClasses = () => {
    if (isScrolled) {
      return 'glass shadow-lg py-2';
    }
    return 'bg-transparent py-4';
  };

  // Mobile menu button color (white on homepage when not scrolled)
  const getMobileMenuBtnColor = () => {
    if (!isScrolled && isHomePage) {
      return 'text-white';
    }
    return 'text-neutral-700';
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${getHeaderClasses()}`}
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="logo-container flex items-center gap-3">
            <img
              src={mockData.images.logo}
              alt="Erste-Hilfe Fokus Logo"
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm uppercase tracking-wide ${
                  isActive(link.path)
                    ? 'text-red-600 font-semibold active'
                    : 'text-neutral-700 hover:text-red-600'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="language-toggle"
              aria-label="Toggle Language"
            >
              <Globe className="w-4 h-4" />
              <span className={language === 'de' ? 'active' : ''}>DE</span>
              <span className="text-neutral-300">|</span>
              <span className={language === 'en' ? 'active' : ''}>EN</span>
            </button>

            {/* Phone */}
            <a
              href={`tel:${mockData.company.phone}`}
              className="flex items-center gap-2 text-neutral-700 hover:text-red-600 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">0159 0226 8861</span>
            </a>

            {/* CTA Button */}
            <a
              href={mockData.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6">
                {t('nav.bookNow')}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 transition-colors ${getMobileMenuBtnColor()}`}
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open Menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <img
              src={mockData.images.logo}
              alt="Logo"
              className="h-10 w-auto"
            />
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close Menu"
            >
              <X className="w-6 h-6 text-neutral-700" />
            </button>
          </div>

          <nav className="flex flex-col gap-4 mb-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`py-3 px-4 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-red-50 text-red-600'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-neutral-100 rounded-lg mb-4"
          >
            <Globe className="w-5 h-5" />
            <span className={language === 'de' ? 'font-semibold text-red-600' : ''}>
              Deutsch
            </span>
            <span>/</span>
            <span className={language === 'en' ? 'font-semibold text-red-600' : ''}>
              English
            </span>
          </button>

          {/* Mobile Phone */}
          <a
            href={`tel:${mockData.company.phone}`}
            className="flex items-center justify-center gap-3 py-3 px-4 bg-neutral-100 rounded-lg mb-4"
          >
            <Phone className="w-5 h-5 text-red-600" />
            <span className="font-medium">0159 0226 8861</span>
          </a>

          {/* Mobile CTA */}
          <a
            href={mockData.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3">
              {t('nav.bookNow')}
            </Button>
          </a>
        </div>
      </div>
    </>
  );
};

export default Header;
